import { Link } from 'react-router'
import { Button } from '#app/components/ui/button'
import { cn } from '#app/utils/misc'

export interface PricingSnapshotTier {
	badge?: string
	name: string
	description: string
	detail?: string
	bullets?: string[]
	highlighted?: boolean
}

export interface PricingSnapshotAction {
	text: string
	href: string
	variant?: 'default' | 'secondary' | 'outline' | 'ghost' | 'link'
}

export interface PricingSnapshotProps {
	eyebrow?: string
	title?: string
	subtitle?: string
	tiers?: PricingSnapshotTier[]
	permanenceNote?: string
	primaryCta?: PricingSnapshotAction
	secondaryCta?: PricingSnapshotAction
	background?: 'default' | 'muted' | 'primary-light'
}

export function PricingSnapshot({
	eyebrow,
	title,
	subtitle,
	tiers = [],
	permanenceNote,
	primaryCta,
	secondaryCta,
	background = 'primary-light',
}: PricingSnapshotProps) {
	const bgClasses = {
		default: 'bg-background',
		muted: 'bg-muted/30',
		'primary-light': 'bg-transparent',
	}

	return (
		<section
			className={cn(
				'relative px-6 py-28 sm:px-8 lg:px-12',
				bgClasses[background],
			)}
		>
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0 -z-10"
				style={{
					background:
						background === 'primary-light'
							? 'linear-gradient(180deg, transparent 0%, var(--landing-veil) 50%, transparent 100%)'
							: undefined,
				}}
			/>
			<div className="mx-auto max-w-4xl">
				<div className="mx-auto max-w-3xl text-center">
					{eyebrow ? (
						<p className="text-label text-muted-foreground">{eyebrow}</p>
					) : null}
					{title ? <h2 className="text-title-2 mt-4">{title}</h2> : null}
					{subtitle ? (
						<p className="text-muted-foreground mx-auto mt-5 max-w-xl text-base leading-relaxed">
							{subtitle}
						</p>
					) : null}
				</div>

				{tiers.length > 0 ? (
					<div className="mt-12 grid gap-5 md:grid-cols-2">
						{tiers.map((tier, index) => {
							const action = tier.highlighted ? secondaryCta : primaryCta

							return (
								<article
									key={`${tier.name}-${index}`}
									className={cn(
										'relative rounded-2xl border bg-white p-8',
										tier.highlighted ? '' : 'border-primary/10',
									)}
									style={
										tier.highlighted
											? {
													borderColor: 'oklch(0.65 0.12 91 / 0.3)',
												}
											: undefined
									}
								>
									{tier.highlighted ? (
										<div
											aria-hidden
											className="absolute inset-x-0 top-0 h-px"
											style={{
												background:
													'linear-gradient(to right, transparent, oklch(0.65 0.12 91 / 0.45), transparent)',
											}}
										/>
									) : null}

									{tier.badge ? (
										<p
											className={cn(
												'inline-block rounded-full border px-3 py-0.5 text-xs font-medium',
												tier.highlighted
													? ''
													: 'border-primary/15 text-muted-foreground',
											)}
											style={
												tier.highlighted
													? {
															borderColor: 'oklch(0.65 0.12 91 / 0.3)',
															backgroundColor: 'oklch(0.65 0.12 91 / 0.08)',
															color: 'var(--gold-text)',
														}
													: undefined
											}
										>
											{tier.badge}
										</p>
									) : null}

									<h3 className="mt-4 text-lg font-semibold">
										{tier.detail ?? tier.name}
									</h3>
									<p className="text-muted-foreground mt-2 text-sm leading-relaxed">
										{tier.description}
									</p>

									{tier.bullets?.length ? (
										<ul className="mt-6 space-y-3">
											{tier.bullets.map((bullet, bulletIndex) => (
												<li
													key={`${tier.name}-${bulletIndex}`}
													className="text-muted-foreground flex items-start gap-3 text-sm leading-relaxed"
												>
													<span
														className="mt-0.5 text-xs"
														style={
															tier.highlighted
																? { color: 'var(--gold-text)' }
																: { color: 'oklch(0 0 0 / 0.3)' }
														}
													>
														✓
													</span>
													{bullet}
												</li>
											))}
										</ul>
									) : null}

									{action ? (
										<Button
											asChild
											variant={
												action.variant ??
												(tier.highlighted ? 'default' : 'outline')
											}
											size="lg"
											className="mt-8 h-11 w-full"
										>
											<Link to={action.href}>{action.text}</Link>
										</Button>
									) : null}
								</article>
							)
						})}
					</div>
				) : null}

				{permanenceNote ? (
					<p className="text-muted-foreground mt-8 text-center text-sm">
						{permanenceNote}
					</p>
				) : null}
			</div>
		</section>
	)
}
